import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { Bell, Radio, Heart, MessageCircle, Sparkles, Megaphone, CheckCheck } from "lucide-react";

const ICONS = { live: Radio, support: Heart, replies: MessageCircle, new_media: Sparkles, admin: Megaphone };

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const loadCount = useCallback(async () => {
    try { const { data } = await api.get("/me/notifications/unread-count"); setCount(data.count); } catch {}
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  const openPanel = async (v) => {
    setOpen(v);
    if (v) {
      try { const { data } = await api.get("/me/notifications"); setItems(data); } catch {}
    }
  };

  const markAll = async () => {
    try { await api.post("/me/notifications/read-all"); setCount(0); setItems((xs) => xs.map((x) => ({ ...x, read: true }))); } catch {}
  };

  const click = async (n) => {
    try { await api.post(`/me/notifications/${n.id}/read`); } catch {}
    setCount((c) => Math.max(0, c - (n.read ? 0 : 1)));
    setOpen(false);
    if (n.meta?.slug) navigate(`/${n.meta.media_type || (n.type === "live" ? "tv" : "radio")}/${n.meta.slug}`);
  };

  return (
    <Popover open={open} onOpenChange={openPanel}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative w-9 h-9 rounded-full hover:bg-secondary flex items-center justify-center">
          <Bell size={18} />
          {count > 0 && (
            <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-rdcred text-white text-[9px] font-bold flex items-center justify-center">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-heading font-bold text-sm">Notifications</span>
          <button onClick={markAll} data-testid="notif-markall" className="text-xs text-primary flex items-center gap-1 hover:underline"><CheckCheck size={13} /> Tout lire</button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">Aucune notification.</p>
          ) : items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.id} onClick={() => click(n)} data-testid={`notif-item-${n.id}`}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border last:border-0 hover:bg-secondary ${!n.read ? "bg-primary/5" : ""}`}>
                <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon size={15} /></span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{n.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString("fr-FR")}</p>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-rdcred shrink-0 mt-1.5" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
